import { AnimatePresence, motion } from "framer-motion";
import React, { useCallback, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useProfile } from "../../../contexts/ProfileContext.tsx";
import Icon, { IconNames } from "../../../core/components/Icon.tsx";
import Separator from "../../../core/components/Separator.tsx";
import useOutsideClick from "../../../hooks/useOutsideClick.ts";
import RotaItem from "./components/DropdownRotaItem.tsx";
import {
	mostAccessedItemsSectionArray,
	navbarSection,
} from "./constants/Dropdown.constants.tsx";
import { RotaItem as RotaItemInterface } from "./interfaces/Dropdown.interfaces.tsx";

interface Section {
	titleSection: string;
	items: RotaItemInterface[];
	icon: IconNames;
}

function DropdownPesquisa() {
	const {
		dadosFilial,
		handleSetContextoEmbarque,
		contextoEmbarque,
	} = useProfile();

	const navigate = useNavigate();
	const location = useLocation();

	const [searchTerm, setSearchTerm] = useState<string>("");
	const [isHovering, setIsHovering] = useState<boolean>(false);

	const [
		isOpenPesquisaMenuItems,
		,
		,
		refPesquisaMenuItems,
		openPesquisaMenuItems,
	] = useOutsideClick();

	const onClickSearchItem = useCallback(
		(item: RotaItemInterface) => {
			if (item.contextoEmbarque && item.contextoEmbarque !== contextoEmbarque) {
				handleSetContextoEmbarque(item.contextoEmbarque);
			}
			navigate(item.rota);
		},
		[handleSetContextoEmbarque, navigate, location.pathname, contextoEmbarque]
	);

	const filialRotaItem: RotaItemInterface[] = useMemo(() => {
		const filteredDadosFilial =
			dadosFilial?.filter((item) => item.contexto !== "templateEmbarque") || [];
		return (
			filteredDadosFilial?.map((item) => ({
				label: item.nomeFilial,
				rota: "/viagens/" + item.contexto,
				contextoEmbarque: item.contexto,
			})) || []
		);
	}, [dadosFilial]);

	const handleSearch = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			setSearchTerm(event.target.value);
		},
		[]
	);

	const allSections = useMemo(() => {
		let sections: Section[] = [];
		if (filialRotaItem.length > 0) {
			sections.push({
				titleSection: "Filiais",
				items: filialRotaItem,
				icon: "Building",
			});
		}
		sections = [
			...mostAccessedItemsSectionArray,
			...sections,
			...navbarSection,
		];
		return sections;
	}, [filialRotaItem]);

	const filteredSections = useMemo(() => {
		return allSections
			.map((section) => {
				const filteredItems = section.items.filter(
					(item) =>
						item.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
						item.rota.toLowerCase().includes(searchTerm.toLowerCase()) ||
						item.contextoEmbarque
							?.toLowerCase()
							.includes(searchTerm.toLowerCase()) ||
						section.titleSection
							.toLowerCase()
							.includes(searchTerm.toLowerCase())
				);
				return {
					...section,
					items: filteredItems,
				};
			})
			.filter((section) => section.items.length > 0);
	}, [allSections, searchTerm]);

	const memoizedTheme = useMemo(() => {
		return isOpenPesquisaMenuItems || isHovering ? "dark" : "light";
	}, [isOpenPesquisaMenuItems, isHovering]);

	return (
		<motion.div
			className="navbar-search-box"
			initial={{ width: 35, backgroundColor: "rgba(0, 0, 0, 0)" }}
			animate={{
				width: isOpenPesquisaMenuItems ? 200 : 35,
			}}
			transition={{ duration: 0.5, backgroundColor: { duration: 0.5 } }}
			onHoverStart={() => setIsHovering(true)}
			onHoverEnd={() => setIsHovering(false)}
			whileHover={{ width: 200, backgroundColor: "#fff" }}
			whileFocus={{ width: 200, backgroundColor: "#fff" }}
		>
			<div
				className="search-box-transition"
				ref={refPesquisaMenuItems}
				onClick={openPesquisaMenuItems}
			>
				<div>
					<div className="search-icon">
						<Icon name="Search" size="lg" theme={memoizedTheme} />
					</div>
					<motion.input
						animate={{ opacity: isOpenPesquisaMenuItems || isHovering ? 1 : 0 }}
						transition={{ duration: 0.2 }}
						type="text"
						placeholder="Pesquisar..."
						value={searchTerm}
						onChange={handleSearch}
						className="search-input"
					/>
				</div>
			</div>
			<AnimatePresence>
				{isOpenPesquisaMenuItems && (
					<motion.div
						className="dropdown-search-container"
						initial={{ opacity: 0, y: -10 }}
						animate={{ opacity: 1, y: 0 }}
						exit={{ opacity: 0, y: -10 }}
						transition={{ duration: 0.2 }}
					>
						<Separator size="xs" />
						<div className="search-results">
							{filteredSections.length ? (
								filteredSections.map((section) => (
									<div key={section.titleSection}>
										<div className="section-title">
											<Icon name={section.icon} size="md" theme="dark" />
											<Separator direction="horizontal" size="xs" />
											<span>{section.titleSection}</span>
										</div>
										{section.items.map((item, itemIndex) => (
											<div
												key={`${item.label} ${itemIndex}`}
												className="botao-menu-cte"
											>
												<RotaItem
													item={item}
													onClick={() => onClickSearchItem(item)}
												/>
												<Icon
													name="SquareArrowOutUpRight"
													theme="dark"
													size="lg"
												/>
											</div>
										))}
										<Separator size="xs" />
									</div>
								))
							) : (
								<div className="search-results-empty">
									<span>Nenhum resultado encontrado</span>
								</div>
							)}
							<Separator size="xs" />
						</div>
					</motion.div>
				)}
			</AnimatePresence>
		</motion.div>
	);
}

export default DropdownPesquisa;
